import { c as _c } from "react/compiler-runtime";
import { getOrgApiKeysOptions, postOrgApiKeysByIdRevealMutation } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { CodeBlock, Select } from '@onefootprint/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import usePermissions from '@/hooks/use-permissions';
import { buildCurlCommand } from './curl-preview.utils';
const MAX_KEYS = 10;
const SECRET_HEADER = 'X-Footprint-Secret-Key';
const SECRET_PLACEHOLDER = '<YOUR_SECRET_API_KEY>';
const DEFAULT_BASE_URL = 'https://api.onefootprint.com';
export type CurlPreviewProps = {
  method: string;
  path: string;
  body?: unknown;
  title?: ReactNode;
};
const CurlPreview = t0 => {
  const $ = _c(49);
  const {
    method,
    path,
    body,
    title
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "curlPreview"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("common", t1);
  const selectId = useId();
  const {
    hasPermission
  } = usePermissions();
  let t2;
  if ($[1] !== hasPermission) {
    t2 = hasPermission("api_keys");
    $[1] = hasPermission;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const canReadApiKeys = t2;
  const showRequestError = useRequestErrorToast();
  const [selectedKeyId, setSelectedKeyId] = useState("");
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getOrgApiKeysOptions({
      query: {
        status: "enabled",
        pageSize: MAX_KEYS
      }
    });
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== canReadApiKeys) {
    t4 = {
      ...t3,
      enabled: canReadApiKeys,
      select: _temp
    };
    $[4] = canReadApiKeys;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const apiKeysQuery = useQuery(t4);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = postOrgApiKeysByIdRevealMutation();
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== showRequestError) {
    t6 = {
      ...t5,
      onError: showRequestError
    };
    $[7] = showRequestError;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const revealMutation = useMutation(t6);
  let t7;
  if ($[9] !== revealMutation) {
    t7 = id => {
      setSelectedKeyId(id);
      if (id) {
        revealMutation.mutate({
          path: {
            id
          }
        });
      }
    };
    $[9] = revealMutation;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleSelectKey = t7;
  const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || DEFAULT_BASE_URL;
  let t8;
  if ($[11] !== apiKeysQuery.data || $[12] !== body || $[13] !== canReadApiKeys || $[14] !== handleSelectKey || $[15] !== method || $[16] !== path || $[17] !== revealMutation.data || $[18] !== selectId || $[19] !== selectedKeyId || $[20] !== t || $[21] !== title) {
    const selectedKey = apiKeysQuery.data?.find(apiKey => apiKey.id === selectedKeyId);
    const scrubbedSecret = selectedKey?.scrubbedKey ?? SECRET_PLACEHOLDER;
    const revealed = revealMutation.data;
    const copySecret = revealed?.id === selectedKeyId ? revealed?.key ?? scrubbedSecret : scrubbedSecret;
    let t9;
    if ($[23] !== body || $[24] !== method || $[25] !== path) {
      t9 = secret => buildCurlCommand({
        method,
        url: `${baseUrl}${path}`,
        headers: {
          [SECRET_HEADER]: secret
        },
        body
      });
      $[23] = body;
      $[24] = method;
      $[25] = path;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    const toCommand = t9;
    let t10;
    if ($[27] !== apiKeysQuery.data) {
      t10 = apiKeysQuery.data ?? [];
      $[27] = apiKeysQuery.data;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    let t11;
    if ($[29] !== t10) {
      t11 = t10.map(_temp2);
      $[29] = t10;
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    const options = t11;
    let t12;
    if ($[31] !== method || $[32] !== path || $[33] !== title) {
      t12 = title ?? `${method.toUpperCase()} ${path}`;
      $[31] = method;
      $[32] = path;
      $[33] = title;
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    const label = t12;
    let t13;
    if ($[35] !== t) {
      t13 = t(_temp3);
      $[35] = t;
      $[36] = t13;
    } else {
      t13 = $[36];
    }
    let t14;
    if ($[37] !== label) {
      t14 = <span className="truncate">{label}</span>;
      $[37] = label;
      $[38] = t14;
    } else {
      t14 = $[38];
    }
    let t15;
    if ($[39] !== canReadApiKeys || $[40] !== handleSelectKey || $[41] !== options || $[42] !== selectId || $[43] !== selectedKeyId || $[44] !== t) {
      t15 = canReadApiKeys && <Select id={selectId} className="pl-2 max-md:max-w-[160px] md:min-w-[280px]" aria-label={t(_temp4)} placeholder={t(_temp5)} value={selectedKeyId} onChange={handleSelectKey} options={options} size="tiny" />;
      $[39] = canReadApiKeys;
      $[40] = handleSelectKey;
      $[41] = options;
      $[42] = selectId;
      $[43] = selectedKeyId;
      $[44] = t;
      $[45] = t15;
    } else {
      t15 = $[45];
    }
    let t16;
    if ($[46] !== t14 || $[47] !== t15) {
      t16 = <>{t14}{t15}</>;
      $[46] = t14;
      $[47] = t15;
      $[48] = t16;
    } else {
      t16 = $[48];
    }
    t8 = <CodeBlock language="bash" ariaLabel={t13} isPrivate={true} contentToCopy={toCommand(copySecret)} title={t16}>{toCommand(scrubbedSecret)}</CodeBlock>;
    $[11] = apiKeysQuery.data;
    $[12] = body;
    $[13] = canReadApiKeys;
    $[14] = handleSelectKey;
    $[15] = method;
    $[16] = path;
    $[17] = revealMutation.data;
    $[18] = selectId;
    $[19] = selectedKeyId;
    $[20] = t;
    $[21] = title;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
};
export default CurlPreview;
function _temp(response) {
  return response.data;
}
function _temp2(apiKey_0) {
  return {
    value: apiKey_0.id,
    label: apiKey_0.name,
    description: `${apiKey_0.role.name} · ${apiKey_0.scrubbedKey.replaceAll("*", "\u2022")}`
  };
}
function _temp3($) {
  return $.copyAriaLabel;
}
function _temp4($_0) {
  return $_0.apiKeyLabel;
}
function _temp5($_1) {
  return $_1.apiKeyPlaceholder;
}